import { InteractiveContainer, Popover, ListBox } from "@jugl-web/ui-kit";
import {
  currencyCodeToSymbol,
  useAppVariant,
  useTranslations,
} from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useMemo, useState } from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { WorkspaceTile } from "../WorkspaceTile";
import { getCurrenciesList } from "../../utils/getCurrenciesList";
import { ReactComponent as SeparatorDotIcon } from "./assets/separator-dot.svg";
import { ConfirmCurrencyChange } from "../ConfirmCurrencyChange";

export const WorkspaceCurrency = () => {
  const { t } = useTranslations();
  const { entitiesApi } = useRestApiProvider();
  const { isCx } = useAppVariant();

  const [currencyToChange, setCurrencyToChange] = useState<string>();
  const [updateEntity] = entitiesApi.useEditEntityMutation();

  const { entity } = useEntitySelectedProvider();

  const currency = useMemo(() => {
    if (entity.currency) {
      return getCurrenciesList().find(
        (currencyObj) => currencyObj.code === entity.currency
      );
    }
    return null;
  }, [entity.currency]);

  const currencyListItems = useMemo(
    () =>
      getCurrenciesList()
        .sort((a, b) => {
          if (a.country_code < b.country_code) return -1;
          if (a.country_code > b.country_code) return 1;
          return 0;
        })
        .map((item) => ({
          id: item.country_code,
          value: {
            code: item.code,
            label: item.name,
            flag: item.flag,
            symbol: item.symbol,
          },
        })),
    []
  );

  const handleCurrencyChange = async (currencyCode: string) => {
    setCurrencyToChange(undefined);
    await updateEntity({
      data: { currency: currencyCode },
      entityId: entity.id,
      meta: {
        successMessage: t({
          id: "feedback.workspace-currency-was-updated",
          defaultMessage: "Workspace currency was updated",
        }),
      },
    });
  };

  return (
    <>
      <Popover
        placement="bottom"
        renderTrigger={({ Trigger, triggerRef }) => (
          <Trigger as={InteractiveContainer} ref={triggerRef}>
            <WorkspaceTile
              tooltipText={
                !isCx
                  ? t({
                      id: "workspace-page.workspace-currency-tooltip",
                      defaultMessage:
                        "Indicated Currency will be used in Services module 💸",
                    })
                  : undefined
              }
              buttonText={
                currency
                  ? t({
                      id: "common.change",
                      defaultMessage: "Change",
                    })
                  : t({
                      id: "workspace-page.set",
                      defaultMessage: "Set",
                    })
              }
            >
              <div className="flex flex-col items-center gap-3 duration-500 group-hover:mt-[-90px]">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#429760] font-bold text-white">
                  {currency?.code ? currencyCodeToSymbol(currency?.code) : null}
                </div>
                <div className="flex flex-col items-center gap-[6px] text-center">
                  {currency && (
                    <span className="flex items-center gap-2.5 font-medium text-dark-800 capitalize">
                      {currency?.code}
                      <SeparatorDotIcon />
                      {currency?.name}
                    </span>
                  )}
                  {!currency && (
                    <span className="font-medium text-dark-800">
                      {t({
                        id: "workspace-page.workspace-currency",
                        defaultMessage: "Workspace Currency",
                      })}
                    </span>
                  )}
                  <span className="text-sm text-[#828282]">
                    {currency &&
                      t({
                        id: "workspace-page.workspace-currency",
                        defaultMessage: "Workspace Currency",
                      })}
                    {!currency &&
                      t({
                        id: "workspace-page.tap-to-set",
                        defaultMessage: "Tap to set",
                      })}
                  </span>
                </div>
              </div>
            </WorkspaceTile>
          </Trigger>
        )}
      >
        {({ onClose }) => (
          <div className="p-4">
            <ListBox
              className="w-[280px]"
              selectionBehavior={{ mode: "single", canToggle: false }}
              onSelect={(item) => {
                setCurrencyToChange(item.value.code);
                onClose();
              }}
              hasSearch
              hasSearchAutoFocus={false}
              renderSearchLabel={(item) =>
                `${item.value.label} ${item.value.code}`
              }
              items={currencyListItems}
              renderLabel={(item) => (
                <div className="flex flex-col font-secondary">
                  <div className="text-base text-dark">{item.id}</div>
                  <div className="text-xs text-[#828282] capitalize">
                    {item.value.label}
                  </div>
                </div>
              )}
              renderStartIcon={(item) => (
                <img
                  src={item.value.flag}
                  alt="flag"
                  loading="lazy"
                  className="h-[20px] w-[28px]"
                />
              )}
              renderEndIcon={(item) => currencyCodeToSymbol(item.value.code)}
              maxVisibleItems={5}
              itemSize="lg"
              spaceBetweenItems="compact"
            />
          </div>
        )}
      </Popover>
      <ConfirmCurrencyChange
        isOpen={Boolean(currencyToChange)}
        currencyCode={currencyToChange}
        onClose={() => setCurrencyToChange(undefined)}
        onSave={() => {
          if (currencyToChange) {
            handleCurrencyChange(currencyToChange);
          }
        }}
      />
    </>
  );
};
